/**
 * RetailerAdminContext
 *
 * The retailer the signed-in member is acting for (/api/retailer-admin-context.php)
 * and what they may do there. Owned by RequireRetailerMember, read by every
 * page under /retaileradmin.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  fetchRetailerContext,
  switchRetailer as apiSwitchRetailer,
  RetailerAccessError,
  type RetailerAdminContext as Ctx,
} from '../../services/retailerAdminApi';

interface Value {
  ctx: Ctx | null;
  loading: boolean;
  /** 'not_member' | 'failed' | null */
  error: 'not_member' | 'failed' | null;
  reload: () => Promise<void>;
  switchRetailer: (retailerId: number) => Promise<void>;
}

const Context = createContext<Value | null>(null);

export const RetailerAdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, refreshSession } = useAuth();
  const [ctx, setCtx] = useState<Ctx | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Value['error']>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCtx(await fetchRetailerContext());
    } catch (e) {
      setCtx(null);
      setError(e instanceof RetailerAccessError ? 'not_member' : 'failed');
    } finally {
      setLoading(false);
    }
  }, []);

  // A fresh session read first: approval may have happened since this tab's
  // session was hydrated, and the header menu keys off the same payload.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refreshSession();
      if (!cancelled) await load();
    })();
    return () => { cancelled = true; };
  }, [user?.sub, refreshSession, load]);

  const switchRetailer = useCallback(async (retailerId: number) => {
    setCtx(await apiSwitchRetailer(retailerId));
  }, []);

  const value = useMemo<Value>(() => ({ ctx, loading, error, reload: load, switchRetailer }), [ctx, loading, error, load, switchRetailer]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useRetailerAdmin = (): Value => {
  const v = useContext(Context);
  if (!v) throw new Error('useRetailerAdmin must be used within RetailerAdminProvider');
  return v;
};

/** The resolved context — only call from pages rendered inside RequireRetailerMember. */
export const useRetailerContext = (): Ctx => {
  const { ctx } = useRetailerAdmin();
  if (!ctx) throw new Error('Retailer context is not loaded');
  return ctx;
};
